import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './css/AddRestaurant.css'; // Ensure this path is correct
import axiosRestaurantInstance from './AxiosRestaurantInstance';
import { normalizeRestaurantShortCode } from './restaurantApi';
import {
  formatFssaiDisplay,
  sanitizeFssaiInput,
  validateFssaiNumber,
} from './formatDisplay';
import RestaurantServicesPicker from './RestaurantServicesPicker';
import {
  buildServicesPayload,
  emptyServiceSelections,
  validateServicesSelection,
} from './restaurantServiceConstants';
import { canManageRestaurantServices } from '../utils/eatsAppAuth';

const ADDRESS_SEARCH_DEBOUNCE_MS = 350;

const AddRestaurant = ({ onClose, createdById, onSuccess, embedded = false }) => {
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem('user'));

    const [restaurantName, setRestaurantName] = useState('');
    const [restaurantShortCode, setRestaurantShortCode] = useState('');
    const [cuisineType, setCuisineType] = useState('');
    const [fssaiNumber, setFssaiNumber] = useState('');
    const [contactNo, setContactNo] = useState('');
    const [email, setEmail] = useState('');
    const [notes, setNotes] = useState('');
    const [status, setStatus] = useState('ACTIVE');
    const [isDefault, setIsDefault] = useState(true);
    const [addressLine1, setAddressLine1] = useState('');
    const [addressLine2, setAddressLine2] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [country, setCountry] = useState('');
    const [longitude, setLongitude] = useState('');
    const [latitude, setLatitude] = useState('');
    const [addressSearch, setAddressSearch] = useState('');
    const [addressSuggestions, setAddressSuggestions] = useState([]);
    const [addressSearchLoading, setAddressSearchLoading] = useState(false);
    const addressSearchTimeoutRef = useRef(null);

    const [isHomeDeliveryAvailable, setIsHomeDeliveryAvailable] = useState(false);
    const [selectedIngredientIds, setSelectedIngredientIds] = useState([]);
    const [serviceSelections, setServiceSelections] = useState(emptyServiceSelections);
    const showServicesPicker = canManageRestaurantServices(user);

    // Address search as user types in the separate Search Address box (debounced)
    const handleAddressSearchChange = (e) => {
        const value = e.target.value;
        setAddressSearch(value);
        setAddressSuggestions([]);

        if (addressSearchTimeoutRef.current) {
            clearTimeout(addressSearchTimeoutRef.current);
            addressSearchTimeoutRef.current = null;
        }

        if (value.trim().length < 2) return;

        addressSearchTimeoutRef.current = setTimeout(async () => {
            addressSearchTimeoutRef.current = null;
            try {
                setAddressSearchLoading(true);
                const response = await axiosRestaurantInstance.get(`/address/suggest`, {
                    params: { address: value.trim() },
                });
                const list = response.data?.suggestions || response.data?.predictions || [];
                setAddressSuggestions(Array.isArray(list) ? list : []);
            } catch (error) {
                console.error('Error fetching address suggestions:', error.response ? error.response.data : error.message);
                setAddressSuggestions([]);
            } finally {
                setAddressSearchLoading(false);
            }
        }, ADDRESS_SEARCH_DEBOUNCE_MS);
    };

    // Handle Address Selection
    const handleAddressSelect = async (placeId) => {
        try {
            // Use organization API for address details (same as organization form)
            const response = await axiosRestaurantInstance.get(`/address/suggest/details`, {
                params: { placeId },
            });

            const placeDetails = response.data;

            if (!placeDetails) {
                alert('Address details not found');
                return;
            }

            // Automatically fill in the form fields with the fetched address details
            setAddressLine1(placeDetails.addressLine1 || '');
            setAddressLine2(placeDetails.addressLine2 || '');
            setPostalCode(placeDetails.postalCode || '');
            setCity(placeDetails.city || '');
            setState(placeDetails.state || '');
            setCountry(placeDetails.country || '');
            setLatitude(placeDetails.latitude ?? placeDetails.lat ?? '');
            setLongitude(placeDetails.longitude ?? placeDetails.lon ?? '');
            setAddressSuggestions([]);
            setAddressSearch('');
        } catch (error) {
            console.error('Error fetching place details:', error.response ? error.response.data : error.message);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!(city || '').trim()) {
            alert('Please enter city.');
            return;
        }

        if (showServicesPicker) {
            const servicesError = validateServicesSelection(serviceSelections);
            if (servicesError) {
                alert(servicesError);
                return;
            }
        }

        const homeDelivery = isHomeDeliveryAvailable;
        const contactDigits = String(contactNo || '').replace(/\D/g, '');
        if (contactDigits.length !== 10) {
            alert('Please enter a valid 10-digit business phone.');
            return;
        }
        const contactNoNum = parseInt(contactDigits, 10);
        const isDefaultBoolean = Boolean(isDefault);
        const shortCode = normalizeRestaurantShortCode(restaurantShortCode);
        if (!shortCode) {
            alert('Please enter a restaurant short code.');
            return;
        }
        const fssaiError = validateFssaiNumber(fssaiNumber, { required: true, label: 'FSSAI number' });
        if (fssaiError) {
            alert(fssaiError);
            return;
        }

        const latParsed =
            latitude !== '' && latitude != null ? parseFloat(String(latitude), 10) : NaN;
        const lonParsed =
            longitude !== '' && longitude != null ? parseFloat(String(longitude), 10) : NaN;
        if (!Number.isFinite(latParsed) || !Number.isFinite(lonParsed)) {
            alert('Latitude and longitude are required. Search and select an address to set them.');
            return;
        }

        // Payload aligned with POST /restaurant
        const effectiveCreatedById = createdById || user?.userId;
        if (!effectiveCreatedById) {
            alert('Created user is missing. Please create/select root user first.');
            return;
        }

        const restaurantData = {
            restaurantName: (restaurantName || '').trim(),
            restaurantShortCode: shortCode,
            contactNo: contactNoNum,
            email: (email || '').trim(),
            cuisineType: (cuisineType || '').trim() || undefined,
            fssaiNumber: sanitizeFssaiInput(fssaiNumber),
            notes: (notes || '').trim() || undefined,
            addressLine1: (addressLine1 || '').trim(),
            addressLine2: (addressLine2 || '').trim(),
            postalCode: (postalCode || '').trim(),
            city: (city || '').trim(),
            state: (state || '').trim(),
            country: (country || '').trim(),
            createdById: effectiveCreatedById,
            isDefault: isDefaultBoolean,
            homeDeliveryAvailable: homeDelivery,
            lat: latParsed,
            lon: lonParsed,
        };

        if (showServicesPicker) {
            const services = buildServicesPayload(serviceSelections);
            if (services.length > 0) {
                restaurantData.services = services;
            }
        }

        try {
            const token = localStorage.getItem('Token');
            const response = await axiosRestaurantInstance.post('/restaurant', restaurantData, {
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { Authorization: `Bearer ${token}` }),
                },
            });

            const ok = response.status === 201 || response.status === 200;
            const message = response.data?.message;
            if (ok) {
                alert(message || 'Restaurant added successfully');
                // Resetting the form fields
                setRestaurantName('');
                setRestaurantShortCode('');
                setCuisineType('');
                setContactNo('');
                setEmail('');
                setNotes('');
                setStatus('ACTIVE');
                setIsDefault('true');
                setAddressLine1('');
                setAddressLine2('');
                setAddressSearch('');
                setCity('');
                setIsHomeDeliveryAvailable(false);
                setState('');
                setPostalCode('');
                setCountry('');
                setLongitude('');
                setLatitude('');
                setServiceSelections(emptyServiceSelections());
                if (typeof onSuccess === 'function') {
                    onSuccess(response?.data || {});
                }
                // Close the form if onClose prop is provided
                if (onClose) {
                    onClose();
                }
            } else {
                alert('Failed to add restaurant');
            }
        } catch (error) {
            console.error('Error adding restaurant:', error.response ? error.response.data : error.message);
            alert(
                error?.response?.data?.message ||
                    error?.response?.data?.error ||
                    'An error occurred while adding the restaurant.'
            );
        }
    };

    const handleClose = () => {
        if (onClose) {
            onClose();
        } else {
            navigate('/home');
        }
    };

    return (
        <div className={`container1${embedded ? ' add-restaurant-embedded' : ''}`}>
            <div className="add-restaurant-container visible">
                <form onSubmit={handleSubmit}>
                    <div className="add-restaurant-form-row">
                        <div className="add-restaurant-form-group">
                            <label>Restaurant Name:</label>
                            <input
                                type="text"
                                value={restaurantName}
                                onChange={(e) => setRestaurantName(e.target.value)}
                                placeholder="Enter restaurant name"
                                required
                            />
                        </div>
                        <div className="add-restaurant-form-group">
                            <label>Restaurant Short Code:</label>
                            <input
                                type="text"
                                value={restaurantShortCode}
                                onChange={(e) => setRestaurantShortCode(e.target.value)}
                                placeholder="Enter unique short code"
                                required
                            />
                        </div>
                        <div className="add-restaurant-form-group">
                            <label>Business Phone:</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                maxLength={10}
                                value={contactNo}
                                onChange={(e) =>
                                    setContactNo(e.target.value.replace(/\D/g, '').slice(0, 10))
                                }
                                placeholder="Enter 10-digit business phone"
                                required
                            />
                        </div>
                    </div>

                    <div className="add-restaurant-form-row">
                        <div className="add-restaurant-form-group">
                            <label>Email:</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter email"
                                required
                            />
                        </div>
                        <div className="add-restaurant-form-group">
                            <label>Cuisine Type:</label>
                            <input
                                type="text"
                                value={cuisineType}
                                onChange={(e) => setCuisineType(e.target.value)}
                                placeholder="Enter cuisine type"
                            />
                        </div>
                        <div className="add-restaurant-form-group">
                            <label>FSSAI Number:</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                maxLength={17}
                                value={formatFssaiDisplay(fssaiNumber)}
                                onChange={(e) => setFssaiNumber(sanitizeFssaiInput(e.target.value))}
                                placeholder="Enter 14-digit FSSAI number"
                                required
                            />
                        </div>
                    </div>

                    <div className="add-restaurant-form-row">
                        <label className="add-restaurant-form-group add-restaurant-checkbox-group">
                            <span className="add-restaurant-checkbox-group-heading">Is Default:</span>
                            <input
                                type="checkbox"
                                className="add-restaurant-checkbox-large"
                                checked={isDefault}
                                onChange={(e) => setIsDefault(e.target.checked)}
                            />
                        </label>
                        <label className="add-restaurant-form-group add-restaurant-checkbox-group">
                            <span className="add-restaurant-checkbox-group-heading">Home Delivery:</span>
                            <input
                                type="checkbox"
                                className="add-restaurant-checkbox-large"
                                checked={isHomeDeliveryAvailable}
                                onChange={(e) => setIsHomeDeliveryAvailable(e.target.checked)}
                            />
                        </label>
                    </div>

                    <div className="add-restaurant-form-row">
                        <div className="add-restaurant-form-group full-width">
                            <label>Notes:</label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Enter any notes"
                            />
                        </div>
                    </div>

                    {/* Address Section - separate Search Address box, then address fields */}
                    <h3>Address</h3>
                    <div className="add-restaurant-form-row add-restaurant-address-search-row">
                        <div className="add-restaurant-form-group add-restaurant-address-search-wrap">
                            <label>Search Address:</label>
                            <input
                                type="text"
                                value={addressSearch}
                                onChange={handleAddressSearchChange}
                                placeholder="Type to search address..."
                                autoComplete="off"
                                className="add-restaurant-address-search-input"
                            />
                            {addressSearchLoading && (
                                <span className="add-restaurant-address-loading">Searching...</span>
                            )}
                            {addressSuggestions.length > 0 && (
                                <ul className="address-suggestions-dropdown">
                                    {addressSuggestions.map((suggestion, index) => {
                                        const placeId = suggestion.place_id || suggestion.placeId;
                                        const text = suggestion.description || suggestion.formatted_address || '';
                                        return (
                                            <li
                                                key={placeId || index}
                                                onClick={() => handleAddressSelect(placeId)}
                                            >
                                                {text}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </div>
                    <div className="add-restaurant-form-row">
                        <div className="add-restaurant-form-group">
                            <label>Address Line 1:</label>
                            <input
                                type="text"
                                value={addressLine1}
                                onChange={(e) => setAddressLine1(e.target.value)}
                                placeholder="Enter address line 1"
                            />
                        </div>
                        <div className="add-restaurant-form-group">
                            <label>Address Line 2:</label>
                            <input
                                type="text"
                                value={addressLine2}
                                onChange={(e) => setAddressLine2(e.target.value)}
                                placeholder="Enter address line 2"
                            />
                        </div>
                        <div className="add-restaurant-form-group">
                            <label>Postal Code:</label>
                            <input
                                type="text"
                                value={postalCode}
                                onChange={(e) => setPostalCode(e.target.value)}
                                placeholder="Enter postal code"
                                required
                            />
                        </div>
                    </div>

                    <div className="add-restaurant-form-row">
                        <div className="add-restaurant-form-group">
                            <label>City:</label>
                            <input
                                type="text"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                placeholder="Enter city"
                                required
                            />
                        </div>
                        <div className="add-restaurant-form-group">
                            <label>State:</label>
                            <input
                                type="text"
                                value={state}
                                onChange={(e) => setState(e.target.value)}
                                placeholder="Enter state"
                                required
                            />
                        </div>
                        <div className="add-restaurant-form-group">
                            <label>Country:</label>
                            <input
                                type="text"
                                value={country}
                                onChange={(e) => setCountry(e.target.value)}
                                placeholder="Enter country"
                                required
                            />
                        </div>
                    </div>

                    <div className="add-restaurant-form-row">
                        <div className="add-restaurant-coords-pair">
                            <div className="add-restaurant-form-group">
                                <label>Longitude:</label>
                                <input
                                    type="text"
                                    value={longitude}
                                    onChange={(e) => setLongitude(e.target.value)}
                                    placeholder="Enter longitude"
                                    readOnly
                                />
                            </div>
                            <div className="add-restaurant-form-group">
                                <label>Latitude:</label>
                                <input
                                    type="text"
                                    value={latitude}
                                    onChange={(e) => setLatitude(e.target.value)}
                                    placeholder="Enter latitude"
                                    readOnly
                                />
                            </div>
                        </div>
                    </div>

                    {showServicesPicker ? (
                        <RestaurantServicesPicker
                            selections={serviceSelections}
                            onChange={setServiceSelections}
                        />
                    ) : null}

                    <div className="add-restaurant-submit-button">
                        <button type="submit">Save</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddRestaurant;
